// Accounts → Overview (/app/money): the demo's Accounts across every development in the user's scope — the manager's
// money view, read-only. One call, GET /accounts/overview, which reads each development through its own Analytics tab
// (the same figures as the development's Accounts and Analytics tabs), plus deposits, the bank and bills where the role
// can read them. Money is per currency: a total row per currency, never GBP and AED added together. The Finance group's
// pages stay as they are; the tiles and alerts link through to them.
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { StatTile } from '../charts/StatTile';
import { Card, PageHeader } from '../ui';
import { MonthBars } from '../accounts/charts';
import { formatMoney } from '../../lib/viz';
import { accountsOverview, moneyList, moneyTile, type AccountsRow } from '../../lib/portfolio';
import { Quiet, SectionHead, Th } from './bits';

// Ten money tiles: two rows of five on a laptop, so a seven-figure rent roll fits.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Alert({ to, children, action }: { to: string; children: ReactNode; action: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-900 transition hover:bg-amber-100/70">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <span className="min-w-0 flex-1">{children}</span>
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-amber-800">{action} <ArrowRight className="h-3 w-3" /></span>
    </Link>
  );
}

export function AccountsOverviewTab() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['/accounts/overview'], queryFn: accountsOverview });
  const canArrears = useCan('arrears.read');
  const o = q.data;

  const header = <PageHeader title="Accounts" />;
  if (q.isLoading) return <>{header}<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">{Array.from({ length: 10 }, (_, i) => <div key={i} className="surface h-[78px] animate-pulse" />)}</div></>;
  if (!o) return <>{header}<div className="py-12 text-center text-sm text-ink-400">{q.error ? (q.error as Error).message : 'Nothing to show'}</div></>;

  const s = o.stats; const v = o.visible;
  const letTotal = o.totals.reduce((t, x) => t + x.let, 0); const lettableTotal = o.totals.reduce((t, x) => t + x.lettable, 0);
  const estimated = o.developments.some((d) => d.feeBasis && d.feeBasis !== 'POSTED');
  const feeHint = estimated ? 'last 12 months, partly estimated' : 'last 12 months';
  const rent = moneyTile(s.rentRollPa, '£0'); const arrears = moneyTile(s.arrears, '£0'); const costs = moneyTile(s.buildingCostsPa, '£0');
  const fee = moneyTile(s.feeIncomePa, '£0'); const away = moneyTile(s.paidToAgents, '£0'); const net = moneyTile(s.netToUsPa, '£0');
  const unpaid = moneyTile(s.invoicesUnpaid?.amount, '£0'); const deposits = moneyTile(s.depositsHeld?.amount, '£0'); const bank = moneyTile(s.unallocated?.amount, '£0');
  // Nothing (or not readable) reads as a quiet dash, so the figures that are there stand out.
  const money = (minor: number | null, currency: string, tone?: string) => !minor ? <span className="text-ink-300">—</span>
    : <span className={tone}>{formatMoney(minor, currency)}</span>;
  const cells = (r: AccountsRow, bold = false) => (
    <>
      <td className="px-2.5 py-2.5 text-right tabular-nums">{r.let} / {r.lettable}</td>
      <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.rentRollPaMinor, r.currency)}</td>
      <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.arrearsMinor, r.currency, 'text-red-600')}</td>
      {v.fees && <>
        <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.managementMinor, r.currency)}</td>
        <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.lettingKeptMinor, r.currency)}</td>
        <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{r.lettingPaidAwayMinor ? <span className="text-ink-600">−{formatMoney(r.lettingPaidAwayMinor, r.currency)}</span> : money(0, r.currency)}</td>
        <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.netToUsMinor, r.currency, `${bold ? 'font-semibold' : 'font-medium'} ${(r.netToUsMinor ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`)}</td>
      </>}
      <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums">{money(r.buildingCostsMinor, r.currency)}</td>
    </>
  );

  return (
    <div className="space-y-2.5">
      {header}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile compact label="Rent roll pa" value={rent.value} hint={rent.hint ?? `${letTotal} of ${lettableTotal} flats let`} />
        <StatTile compact label="Arrears" value={arrears.value} hint={arrears.hint ?? (o.arrears ? `${plural(o.arrears.count, 'tenancy', 'tenancies')} being chased` : 'rent owed')}
          status={s.arrears.length ? 'critical' : undefined} onClick={canArrears ? () => navigate('/app/arrears') : undefined} />
        {s.feeIncomePa && <StatTile compact label="Fee income pa" value={fee.value} hint={fee.hint ?? feeHint} />}
        {s.paidToAgents && <StatTile compact label="Paid to agents" value={away.value} hint={away.hint ?? 'letting fees paid away'} />}
        {s.netToUsPa && <StatTile compact label="Net to us pa" value={net.value} hint={net.hint ?? 'our income, after agents'} status={s.netToUsPa.length ? 'good' : undefined} />}
        <StatTile compact label="Building costs pa" value={costs.value} hint={costs.hint ?? 'last 12 months, spent'} />
        {s.invoicesUnpaid && <StatTile compact label="Invoices unpaid" value={unpaid.value} hint={unpaid.hint ?? plural(s.invoicesUnpaid.count, 'bill')}
          status={s.invoicesUnpaid.count ? 'warning' : undefined} onClick={() => navigate('/app/bills?filter=unpaid')} />}
        {s.depositsHeld && <StatTile compact label="Deposits held" value={deposits.value} hint={deposits.hint ?? plural(s.depositsHeld.count, 'deposit')} onClick={() => navigate('/app/money?sub=deposits')} />}
        {s.unallocated && <StatTile compact label="Unallocated bank money" value={bank.value} hint={bank.hint ?? plural(s.unallocated.count, 'payment')}
          status={s.unallocated.count ? 'warning' : undefined} onClick={() => navigate('/app/banking')} />}
        {s.agentInvoicesWaiting && <StatTile compact label="Agent invoices waiting" value={s.agentInvoicesWaiting.count} hint={s.agentInvoicesWaiting.count ? moneyList(s.agentInvoicesWaiting.amount) : 'to approve'}
          status={s.agentInvoicesWaiting.count ? 'warning' : undefined} onClick={() => navigate('/app/bills?filter=agentDraft')} />}
      </div>

      {(!!s.unallocated?.count || !!s.agentInvoicesWaiting?.count) && (
        <div className="space-y-2">
          {!!s.unallocated?.count && <Alert to="/app/banking" action="Allocate it"><b className="font-semibold">{moneyList(s.unallocated.amount)}</b> has come into the bank and is not allocated to a flat yet ({plural(s.unallocated.count, 'payment')}).</Alert>}
          {!!s.agentInvoicesWaiting?.count && <Alert to="/app/bills?filter=agentDraft" action="Review them"><b className="font-semibold">{plural(s.agentInvoicesWaiting.count, 'agent invoice')}</b> waiting to be approved — {moneyList(s.agentInvoicesWaiting.amount)}.</Alert>}
        </div>
      )}

      <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-2.5">
          {o.chart.length ? o.chart.map((c) => (
            <Card key={c.currency} title={`Rent received against building spend${o.chart.length > 1 ? ` · ${c.currency}` : ''}`} subtitle="The last twelve months, every development">
              <MonthBars data={c.months} currency={c.currency} series={[{ key: 'rentMinor', label: 'Rent received' }, { key: 'spendMinor', label: 'Building spend', colour: '#eb6834' }]} />
            </Card>
          )) : <Card title="Rent received against building spend"><Quiet>Nothing received or spent in the last twelve months.</Quiet></Card>}
        </div>
        <aside className="min-w-0">
          <Card title="Whose money is it?">
            <div className="space-y-2.5 text-[12.5px] leading-relaxed text-ink-600">
              <p>Rent is the landlord's money: we collect it for the company that owns each development.</p>
              <p>Only the fees are Lotus Living's income — the management fee and the letting fees we keep. Net to us is those, less the letting fees paid away to agents.</p>
              <p>Building costs are what the developments cost to run: contractor bills paid and the running costs on each company's ledger.</p>
              {canArrears && <p>Chasing arrears happens on the <Link to="/app/arrears" className="font-medium text-bronze-600 hover:underline">Arrears</Link> page (the reminder ladder, promises and notes).</p>}
            </div>
          </Card>
        </aside>
      </div>

      <section className="surface min-w-0 overflow-hidden">
        <SectionHead title="By development" note={v.fees ? `fees over the last 12 months${estimated ? ', partly estimated' : ''}` : 'fee figures need the Fees permission'} />
        {!o.developments.length ? <Quiet>No development in your scope.</Quiet> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-[12.5px]">
              <thead>
                <tr className="bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500">
                  <th className="px-3 py-1.5 text-left">Development</th>
                  <Th>Let</Th><Th>Rent roll pa</Th><Th>Arrears</Th>
                  {v.fees && <><Th>Management fee</Th><Th>Letting kept</Th><Th>Paid away</Th><Th>Net to us</Th></>}
                  <Th>Building costs</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {o.developments.map((d) => (
                  <tr key={d.propertyId} className="hover:bg-ink-50/60">
                    <td className="px-3 py-2">
                      <Link to={`/app/properties/${d.propertyId}?tab=accounts`} className="font-medium text-ink-900 hover:text-bronze-700 hover:underline">{d.name}</Link>
                      {d.city && <div className="text-[11px] text-ink-400">{d.city}</div>}
                    </td>
                    {cells(d)}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-ink-200 bg-ink-50/60 font-medium">
                {o.totals.map((t) => (
                  <tr key={t.currency}>
                    <td className="px-3 py-1.5 text-ink-900">Portfolio total{o.totals.length > 1 ? ` · ${t.currency}` : ''}</td>
                    {cells(t, true)}
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
